import { Building, Calendar, Globe, Pencil, Server } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Organization, OrganizationUpdate, ForgeClient } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import { Label } from '@/components/Common/label';
import { ForgeClientSelector } from '@/components/Forge';
import forgeService from '@/services/forgeService';
import configurationService from '@/services/configurationService';
import { useIsSuperAdmin } from '@/stores/authStore';
import { useForgeApiEnabled } from '@/stores/forgeApiStore';

interface OrganizationGeneralInformationProps {
  organization: Organization;
  canEdit: boolean;
  isEditing: boolean;
  formData: OrganizationUpdate;
  saving: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onFormChange: (data: OrganizationUpdate) => void;
  /** When true, renders form/view fields without the surrounding Card. */
  bare?: boolean;
}

const EMPTY = '—';

const formatDate = (dateString: string, locale: string) =>
  new Date(dateString).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

const Field: React.FC<{
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}> = ({ label, icon: Icon, children }) => (
  <div>
    <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
    <dd className="mt-1 flex items-center gap-2 text-sm">
      {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
      {children}
    </dd>
  </div>
);

export const OrganizationGeneralInformation: React.FC<OrganizationGeneralInformationProps> = ({
  organization,
  canEdit,
  isEditing,
  formData,
  saving,
  onEdit,
  onSave,
  onCancel,
  onFormChange,
  bare = false,
}) => {
  const { t, i18n } = useTranslation();
  const isSuperAdmin = useIsSuperAdmin();
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const [selectedClient, setSelectedClient] = useState<ForgeClient | null>(null);
  const [sectorTemplates, setSectorTemplates] = useState<Array<{ id: string; name: string }>>([]);

  // Fetch client info when organization has forge_client_id
  useEffect(() => {
    const fetchClientInfo = async () => {
      if (forgeApiEnabled && organization.forge_client_id) {
        try {
          const client = await forgeService.getClient(organization.forge_client_id);
          setSelectedClient(client);
        } catch (err) {
          console.error('Error fetching Forge client:', err);
        }
      } else {
        setSelectedClient(null);
      }
    };
    fetchClientInfo();
  }, [organization.forge_client_id, forgeApiEnabled]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await configurationService.getTemplates();
        setSectorTemplates((response.templates ?? []).map((template) => ({ id: template.id, name: template.name })));
      } catch (err) {
        console.error('Error loading sector templates:', err);
      }
    };
    void loadTemplates();
  }, []);

  // Show the template's name, not its id; fall back to the id until templates load.
  const sectorName = organization.sector_template_id
    ? sectorTemplates.find((tpl) => tpl.id === organization.sector_template_id)?.name ??
      organization.sector_template_id
    : null;

  const handleForgeClientSelect = (clientId: string | null, client: ForgeClient | null) => {
    setSelectedClient(client);
    onFormChange({ ...formData, forge_client_id: clientId });
  };

  const body = (
    <>
      {isEditing ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">{t('organizationGeneralInfo.name')} *</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="description">{t('organizationGeneralInfo.description')}</Label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
                rows={3}
                className="mt-1 block w-full rounded-md border border-border bg-background p-2 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="domain">{t('organizationGeneralInfo.domain')}</Label>
              <Input
                id="domain"
                value={formData.domain || ''}
                onChange={(e) => onFormChange({ ...formData, domain: e.target.value })}
                className="mt-1"
                placeholder={t('organizationGeneralInfo.domainPlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="sector_template_id">{t('organizationGeneralInfo.sectorLabel')}</Label>
              <select
                id="sector_template_id"
                value={formData.sector_template_id || ''}
                onChange={(e) => onFormChange({ ...formData, sector_template_id: e.target.value })}
                className="mt-1 block h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">{t('organizationGeneralInfo.sectorPlaceholder')}</option>
                {sectorTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                {t('organizationGeneralInfo.sectorWarning')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active || false}
                onChange={(e) => onFormChange({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="is_active">{t('organizationGeneralInfo.activeOrganization')}</Label>
            </div>
            {isSuperAdmin && forgeApiEnabled && (
              <div className="pt-4 border-t border-border">
                <ForgeClientSelector
                  selectedClientId={formData.forge_client_id || organization.forge_client_id}
                  onSelect={handleForgeClientSelect}
                  className="mb-2"
                />
                <p className="text-xs text-muted-foreground">
                  {t('organizationGeneralInfo.linkForgeHelp')}
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onCancel} disabled={saving}>
                {t('organizationGeneralInfo.cancel')}
              </Button>
              <Button onClick={onSave} disabled={saving}>
                {saving ? t('organizationGeneralInfo.saving') : t('organizationGeneralInfo.save')}
              </Button>
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
            <Field label={t('organizationGeneralInfo.name')}>
              <span className="font-semibold">{organization.name}</span>
            </Field>
            <Field label={t('organizationGeneralInfo.status')}>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                organization.is_active
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}>
                {organization.is_active ? t('organizationGeneralInfo.active') : t('organizationGeneralInfo.inactive')}
              </span>
            </Field>
            <Field label={t('organizationGeneralInfo.description')}>
              {organization.description || EMPTY}
            </Field>
            <Field label={t('organizationGeneralInfo.createdAt')} icon={Calendar}>
              {formatDate(organization.created_at, i18n.language)}
            </Field>
            <Field label={t('organizationGeneralInfo.domain')} icon={Globe}>
              {organization.domain || EMPTY}
            </Field>
            <Field label={t('organizationGeneralInfo.lastModified')} icon={Calendar}>
              {organization.updated_at ? formatDate(organization.updated_at, i18n.language) : EMPTY}
            </Field>
            <Field label={t('organizationGeneralInfo.sectorLabel')}>
              {sectorName ?? EMPTY}
            </Field>
            {/* In the overview the Forge client already has its own stat tile. */}
            {forgeApiEnabled && !bare && (
              <Field label={t('organizationGeneralInfo.forgeClient')} icon={Server}>
                {selectedClient ? (
                  selectedClient.name
                ) : (
                  <span className="text-muted-foreground italic">{t('organizationGeneralInfo.notConfigured')}</span>
                )}
              </Field>
            )}
          </dl>
        )}
    </>
  );

  // The overview card header carries the Edit button.
  if (bare) return body;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            {t('organizationGeneralInfo.title')}
          </CardTitle>
          {canEdit && !isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="flex items-center gap-2"
            >
              <Pencil className="h-4 w-4" />
              {t('organizationGeneralInfo.edit')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};
